// Handler interface
interface Handler {
  setNextHandler(nextHandler: Handler): void
  handleData(data: string): string
}

abstract class RgbaColorHandler implements Handler {
  protected nextHandler: Handler | null = null

  setNextHandler(nextHandler: Handler): void {
    this.nextHandler = nextHandler
  }

  abstract handleData(data: string): string
}

/**
 * @description convert "rgba(rgb(0,0,0), 0.5)" into "rgba(0,0,0,0.5)"
 */
class RgbColorInRgbaColorHandler extends RgbaColorHandler {
  handleData(data: string): string {
    let updatedData = data

    // Regular expression pattern to match and capture the RGB values and alpha\
    const pattern = /rgba\(rgb\((\d+,\s*\d+,\s*\d+)\),\s*([\d.]+)\)/
    if (pattern.test(data)) {
      // Replace the input string using the pattern and the desired format
      updatedData = data.replace(pattern, (match: any, rgbValues, alpha) => {
        return `rgba(${rgbValues},${alpha})`
      })
    }
    if (this.nextHandler) {
      return this.nextHandler.handleData(updatedData)
    }

    return updatedData
  }
}

/**
 * @description convert "rgba(rgba(0,0,0, 0.87), 0.38)" into "rgba(0, 0, 0, 0.3306)"
 */
class RgbaColorInGrbaColorHandler extends RgbaColorHandler {
  handleData(data: string): string {
    let updatedData = data
    const pattern = /rgba\(rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\),\s*([\d.]+)\)/;
    const match = data.match(pattern);

    if (match) {
      const innerRed = parseInt(match[1]);
      const innerGreen = parseInt(match[2]);
      const innerBlue = parseInt(match[3]);
      const innerAlpha = parseFloat(match[4]);
      const outerAlpha = parseFloat(match[5]);
      updatedData = `rgba(${innerRed}, ${innerGreen}, ${innerBlue}, ${innerAlpha * outerAlpha})`;
    }

    if (this.nextHandler) {
      return this.nextHandler.handleData(updatedData)
    }

    return updatedData
  }
}

/**
 * @description convert "rgba(#000000, 0.5)" into "rgba(0,0,0,0.5)"
 */
class HexColorInGrbaColorHandler extends RgbaColorHandler {
  handleData(data: string): string {
    let updatedData = data

    const pattern = /rgba\(#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}),\s*([\d.]+)\)/

    if (pattern.test(data)) {

      const match = data.match(pattern)

      if (match) {
        const hexColor = match[1]
        const alpha = parseFloat(match[2])

        let r, g, b

        if (hexColor.length === 3) {
          // In case #000
          r = parseInt(hexColor[0] + hexColor[0], 16)
          g = parseInt(hexColor[1] + hexColor[1], 16)
          b = parseInt(hexColor[2] + hexColor[2], 16)
        } else if (hexColor.length === 6) {
          // In case #000000
          r = parseInt(hexColor.substring(0, 2), 16)
          g = parseInt(hexColor.substring(2, 4), 16)
          b = parseInt(hexColor.substring(4, 6), 16)
        }

        updatedData = `rgba(${r},${g},${b},${alpha})`
      }
    }
    if (this.nextHandler) {
      return this.nextHandler.handleData(updatedData)
    }

    return updatedData
  }
}

class ChainOfRgbaColorHandlers {
  private rgbHandler: RgbColorInRgbaColorHandler
  private hexHandler: HexColorInGrbaColorHandler
  private rgbaHandler: RgbaColorInGrbaColorHandler
  constructor() {
    this.rgbHandler = new RgbColorInRgbaColorHandler()
    this.hexHandler = new HexColorInGrbaColorHandler()
    this.rgbaHandler = new RgbaColorInGrbaColorHandler()
    this.rgbHandler.setNextHandler(this.hexHandler)
    this.hexHandler.setNextHandler(this.rgbaHandler);
  }

  handleData(data: string) {
    return this.rgbHandler.handleData(data)
  }
}

export default class ConfigProcessor {
  private hasChange: boolean
  private colorHandler: ChainOfRgbaColorHandlers

  constructor() {
    this.hasChange = false
    this.colorHandler = new ChainOfRgbaColorHandlers()
  }

  private traverseNestedKeys(obj: any, currentConfig: any, allConfig: any) {
    for (const key in obj) {
      const value = obj[key]
      if (this.isObjectWithValueProperty(value)) {
        this.handleValueObject({ valueObject: value, key, currentConfig, allConfig })
      } else if (this.isObject(value)) {
        this.handleNestedObject({ nestedObject: value, key, currentConfig, allConfig })
      } else if (this.isString(value)) {
        this.handleStringValue({ currentString: value, key, currentConfig, allConfig })
      }
    }
  }

  isObjectWithValueProperty(obj: any) {
    return this.isObject(obj) && obj.hasOwnProperty('value')
  }

  isObject(obj: any) {
    return typeof obj === 'object' && obj !== null
  }

  isString(str: any) {
    return typeof str === 'string'
  }

  private handleValueObject({
    valueObject,
    key,
    currentConfig,
    allConfig
  }: {
    valueObject: any
    key: string
    currentConfig: any
    allConfig: any
  }) {
    if (this.isString(valueObject.value)) {
      this.handleStringValue({
        currentString: valueObject.value,
        key,
        currentConfig,
        allConfig
      })
    } else if (this.isObject(valueObject.value)) {
      if (!currentConfig[key]) currentConfig[key] = {}
      this.traverseNestedKeys(valueObject.value, currentConfig[key], allConfig)
    }
  }

  private handleNestedObject({
    nestedObject,
    key,
    currentConfig,
    allConfig
  }: {
    nestedObject: any
    key: string
    currentConfig: any
    allConfig: any
  }) {
    if (!currentConfig[key]) currentConfig[key] = {}
    this.traverseNestedKeys(nestedObject, currentConfig[key], allConfig)
  }

  private handleStringValue({
    currentString,
    key,
    currentConfig,
    allConfig
  }: {
    currentString: any
    key: string
    currentConfig: any
    allConfig: any
  }) {
    const oldValue = currentConfig[key]
    let newValue = this.replaceInterpolatedString(currentString, allConfig)

    newValue = this.colorHandler.handleData(newValue)

    currentConfig[key] = newValue
    if (oldValue !== newValue) {
      this.hasChange = true
    }
  }

  traverseAll(jsonObject: any, initialConfig: any) {
    let i = 1
    const MAX_PROCESS_TIME = 20
    const cloneConfig = JSON.parse(JSON.stringify(initialConfig))

    /**
     * Need to loop here because because the colors can be interdependent
     * Ex: { button: "{primary.main}", primary: { main: { value: "{gray.200}"}}, gray: { "200": "#000" }}
     * primary.main depend on gray value
     * button color is depend on primary.main value
     * So it is not possible to replace all dependent values ​​in one iteration
     */

    do {
      this.hasChange = false
      this.traverseNestedKeys(jsonObject, cloneConfig, cloneConfig)
      i++
    } while (this.hasChange && i < MAX_PROCESS_TIME)

    return cloneConfig
  }

  /**
   *
   * @param template
   * @param variables
   * @description replace "{primary.main}" into value like '#000000'
   */
  replaceInterpolatedString(template: string, variables: any) {
    return template.replace(/\{([\w-.]+)\}/g, (match, variableName) => {
      const value = variableName.split('.').reduce((obj: any, key: string) => obj?.[key], variables)

      return value !== undefined ? value : match
    })
  }
}
